'use client';

import { AlertTriangle, Loader2, Mic } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useVoiceConsentStore } from './useVoiceConsentStore';

// The four disclosure bullets, ordered. Keys resolve to the consent copy in the
// `privacy` catalog (byte-identical to the former VOICE_CONSENT_DISCLOSURE.bullets).
const BULLET_KEYS = ['bullet1', 'bullet2', 'bullet3', 'bullet4'] as const;

interface VoiceConsentDialogProps {
  /** Called after consent is successfully recorded — e.g. retry starting the call. */
  onConsented?: () => void;
}

/**
 * R28-P5 first-run voice-consent disclosure (Art. 9(2)(a) explicit consent). Driven
 * by the shared voice-consent store's `promptOpen` flag, so any surface (the chat
 * mic gate, a settings deep-link) can raise it. Discloses the ElevenLabs US transfer
 * BEFORE consent is recorded; "I consent" calls the server grant endpoint and, on
 * success, fires onConsented so the caller can proceed (start the call).
 */
export function VoiceConsentDialog({ onConsented }: VoiceConsentDialogProps) {
  const t = useTranslations('privacy');
  const open = useVoiceConsentStore((s) => s.promptOpen);
  const mutating = useVoiceConsentStore((s) => s.mutating);
  const error = useVoiceConsentStore((s) => s.error);
  const grant = useVoiceConsentStore((s) => s.actions.grant);
  const closePrompt = useVoiceConsentStore((s) => s.actions.closePrompt);

  const handleAccept = async () => {
    const ok = await grant();
    if (ok) onConsented?.();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : closePrompt())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5 text-accent" aria-hidden />
            {t('voiceConsentDialog.title')}
          </DialogTitle>
          <DialogDescription>{t('voiceConsentDialog.summary')}</DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 py-1 text-sm text-muted-foreground">
          {BULLET_KEYS.map((k) => (
            <li key={k} className="flex gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" aria-hidden />
              <span>{t(`voiceConsentDialog.${k}`)}</span>
            </li>
          ))}
        </ul>

        {error && (
          <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={closePrompt} disabled={mutating}>
            {t('voiceConsentDialog.cancelLabel')}
          </Button>
          <Button variant="primary" onClick={handleAccept} disabled={mutating}>
            {mutating && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />}
            {t('voiceConsentDialog.acceptLabel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
